import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useProgression } from '../auth/ProgressionProvider';
import { useSeason } from '../auth/SeasonProvider';
import { resolveModeFromPath } from '../game/modeRouting';
import { summariseProgressionByMode } from '@shared/progression';
import { modeLabel } from '@shared/chart';
import { DiscordInviteLink } from './DiscordInviteLink';
import { TopNavVolume } from './TopNavVolume';
import { CROWN_GLYPH, CUP_GLYPH, STAR_GLYPH } from './currencyGlyphs';
import './topNav.css';

export function TopNav() {
  const { user, signInWithGoogle, signOut, status } = useAuth();
  const { progression } = useProgression();
  const { standingFor } = useSeason();
  const [signInError, setSignInError] = useState<string | null>(null);
  const totalStars = progression.totals.stars;
  const totalCups = progression.totals.cups;
  // Crowns are season-derived (top-3 leaderboard standings), not progression
  // milestones, so they come from the SeasonProvider rather than the
  // per-map progression cache.
  const overallCrowns = standingFor('overall').crowns;
  const showTotals = totalStars > 0 || totalCups > 0 || overallCrowns > 0;
  const location = useLocation();
  // Mode-context badge only renders inside a mode subtree (`/pulse/*`,
  // `/circles/*`). On `/`, `/profile`, `/calibrate` etc. the player
  // is between modes — showing a badge there would be misleading.
  const urlMode = resolveModeFromPath(location.pathname);
  const modeStats = urlMode
    ? summariseProgressionByMode(progression.perMap, urlMode)
    : null;
  const modeCrowns = urlMode ? standingFor(urlMode).crowns : 0;

  const handleGoogleSignIn = async () => {
    setSignInError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign in failed';
      setSignInError(msg);
    }
  };

  return (
    <nav className="top-nav" data-testid="top-nav">
      <Link to="/" className="top-nav-brand" aria-label="BeatHeaven mode picker">
        BeatHeaven
      </Link>
      {urlMode && (
        <div className="top-nav-mode" data-testid="top-nav-mode">
          <span
            className={`top-nav-mode-badge top-nav-mode-badge-${urlMode}`}
            data-testid="top-nav-mode-badge"
          >
            {modeLabel(urlMode)}
          </span>
        </div>
      )}
      <div className="top-nav-links">
        {/*
          "Home" routes back to the mode picker (`/`) — the global
          front door for the app. Mode-specific home pages
          (`/pulse`, `/circles`) are reached by tapping a mode card
          on the picker. Browse / Build live inside their respective
          mode home pages so the universal nav stays mode-agnostic.
        */}
        <NavLink to="/" end>
          Home
        </NavLink>
        <NavLink to="/seasons">Seasons</NavLink>
        <NavLink to="/calibrate">Calibrate</NavLink>
        <NavLink to="/profile">Profile</NavLink>
      </div>
      <div className="top-nav-user">
        <DiscordInviteLink />
        <TopNavVolume />
        {showTotals && (
          <Link
            to="/seasons"
            className="top-nav-trophies"
            data-testid="top-nav-trophies"
            title="Stars, cups & crowns — open Seasons"
            aria-label="View your season standings"
          >
            <span
              className="top-nav-trophies-row top-nav-trophies-overall"
              title="Official map rewards across all modes"
            >
              <span className="top-nav-trophy top-nav-trophy-star" aria-hidden="true">
                {STAR_GLYPH}
              </span>
              <span data-testid="top-nav-stars">{totalStars}</span>
              <span className="top-nav-trophy top-nav-trophy-cup" aria-hidden="true">
                {CUP_GLYPH}
              </span>
              <span data-testid="top-nav-cups">{totalCups}</span>
              {overallCrowns > 0 && (
                <>
                  <span className="top-nav-trophy top-nav-trophy-crown" aria-hidden="true">
                    {CROWN_GLYPH}
                  </span>
                  <span data-testid="top-nav-crowns">{overallCrowns}</span>
                </>
              )}
            </span>
            {urlMode &&
              modeStats &&
              (modeStats.stars > 0 || modeStats.cups > 0 || modeCrowns > 0) && (
                <span
                  className={`top-nav-trophies-row top-nav-trophies-mode top-nav-trophies-mode-${urlMode}`}
                  data-testid="top-nav-trophies-mode"
                  title={`${modeLabel(urlMode)} stars, cups & crowns`}
                >
                  <span className="top-nav-trophies-mode-label">
                    {modeLabel(urlMode)}
                  </span>
                  <span className="top-nav-trophy top-nav-trophy-star" aria-hidden="true">
                    {STAR_GLYPH}
                  </span>
                  <span data-testid="top-nav-mode-stars">{modeStats.stars}</span>
                  <span className="top-nav-trophy top-nav-trophy-cup" aria-hidden="true">
                    {CUP_GLYPH}
                  </span>
                  <span data-testid="top-nav-mode-cups">{modeStats.cups}</span>
                  {modeCrowns > 0 && (
                    <>
                      <span className="top-nav-trophy top-nav-trophy-crown" aria-hidden="true">
                        {CROWN_GLYPH}
                      </span>
                      <span data-testid="top-nav-mode-crowns">{modeCrowns}</span>
                    </>
                  )}
                </span>
              )}
          </Link>
        )}
        {status === 'initialising' && <span className="muted">Signing in...</span>}
        {signInError && (
          <span className="err-pill" title={signInError} data-testid="signin-error">
            Sign-in failed
          </span>
        )}
        {user && user.isAnonymous && (
          <>
            <span className="muted">Guest</span>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleGoogleSignIn()}
            >
              Sign in with Google
            </button>
          </>
        )}
        {user && !user.isAnonymous && (
          <>
            <span>{user.displayName ?? user.email ?? user.uid.slice(0, 6)}</span>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                void signOut();
              }}
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </nav>
  );
}
